/**
 * Timeline Behavior Editor
 * Transport controls (play/pause, time scrubber) + data fields
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { TimelineData } from 'trace-viewer/behaviors';
import { Timeline } from 'trace-viewer/behaviors';

// ============================================================================
// Styles
// ============================================================================

const styles = {
    input: {
        padding: '4px 8px',
        backgroundColor: '#333',
        border: '1px solid #555',
        borderRadius: '3px',
        color: '#fff',
        fontSize: '12px',
        outline: 'none',
    } as React.CSSProperties,
    label: {
        fontSize: '12px',
        color: '#aaa',
        minWidth: '100px',
        flexShrink: 0,
    } as React.CSSProperties,
    fieldRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
    } as React.CSSProperties,
};

// ============================================================================
// Helpers
// ============================================================================

/** Format seconds as m:ss.t */
function formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toFixed(1).padStart(4, '0')}`;
}

// ============================================================================
// Timeline Editor Component
// ============================================================================

export interface TimelineEditorProps {
    data: TimelineData;
    behavior: Timeline;
    onChange: (data: TimelineData) => void;
}

export const TimelineEditor: React.FC<TimelineEditorProps> = ({ data, behavior, onChange }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [isScrubbing, setIsScrubbing] = useState(false);
    const scrubberRef = useRef<HTMLDivElement>(null);

    const duration = data.duration ?? 10;

    // Poll playing state and current time
    useEffect(() => {
        const update = () => {
            setIsPlaying(behavior.isPlaying());
            if (!isScrubbing) {
                setCurrentTime(behavior.getTime());
            }
        };

        update();
        const id = setInterval(update, 50);
        return () => clearInterval(id);
    }, [behavior, isScrubbing]);

    // ── Transport controls ──

    const handlePlayPause = useCallback(() => {
        if (behavior.isPlaying()) {
            behavior.pause();
            setIsPlaying(false);
        } else {
            behavior.play();
            setIsPlaying(true);
        }
    }, [behavior]);

    const handleStop = useCallback(() => {
        behavior.pause();
        behavior.seek(0);
        setIsPlaying(false);
        setCurrentTime(0);
    }, [behavior]);

    // ── Scrubber interaction ──

    const seekFromPointer = useCallback((clientX: number) => {
        const el = scrubberRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const time = fraction * duration;
        behavior.seek(time);
        setCurrentTime(time);
    }, [behavior, duration]);

    const handleScrubStart = useCallback((e: React.PointerEvent) => {
        setIsScrubbing(true);
        seekFromPointer(e.clientX);
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
    }, [seekFromPointer]);

    const handleScrubMove = useCallback((e: React.PointerEvent) => {
        if (!isScrubbing) return;
        seekFromPointer(e.clientX);
    }, [isScrubbing, seekFromPointer]);

    const handleScrubEnd = useCallback(() => {
        setIsScrubbing(false);
    }, []);

    // ── Data field handlers ──

    const handleDurationChange = useCallback((value: number) => {
        onChange({ ...data, duration: Math.max(0, value) });
    }, [data, onChange]);

    const handleSpeedChange = useCallback((value: number) => {
        onChange({ ...data, speed: Math.max(0, value) });
    }, [data, onChange]);

    const handleLoopChange = useCallback((loop: boolean) => {
        onChange({ ...data, loop });
    }, [data, onChange]);

    const handlePlayOnStartChange = useCallback((playOnStart: boolean) => {
        onChange({ ...data, playOnStart });
    }, [data, onChange]);

    const progress = duration > 0 ? currentTime / duration : 0;

    return (
        <div>
            {/* Transport bar */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginBottom: '10px',
            }}>
                <button
                    onClick={handlePlayPause}
                    style={{
                        width: '28px',
                        height: '28px',
                        padding: 0,
                        backgroundColor: isPlaying ? '#ff9800' : '#4caf50',
                        border: 'none',
                        borderRadius: '3px',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '14px',
                        lineHeight: '28px',
                        textAlign: 'center',
                        flexShrink: 0,
                    }}
                    title={isPlaying ? 'Pause' : 'Play'}
                >
                    {isPlaying ? '⏸' : '▶'}
                </button>
                <button
                    onClick={handleStop}
                    style={{
                        width: '28px',
                        height: '28px',
                        padding: 0,
                        backgroundColor: '#555',
                        border: 'none',
                        borderRadius: '3px',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '14px',
                        lineHeight: '28px',
                        textAlign: 'center',
                        flexShrink: 0,
                    }}
                    title="Stop"
                >
                    ⏹
                </button>
                <span style={{
                    fontSize: '11px',
                    color: '#ccc',
                    fontFamily: 'monospace',
                    minWidth: '70px',
                    flexShrink: 0,
                }}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                </span>
            </div>

            {/* Scrubber */}
            <div
                ref={scrubberRef}
                onPointerDown={handleScrubStart}
                onPointerMove={handleScrubMove}
                onPointerUp={handleScrubEnd}
                onPointerCancel={handleScrubEnd}
                style={{
                    position: 'relative',
                    height: '20px',
                    backgroundColor: '#1e1e1e',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    marginBottom: '12px',
                    overflow: 'hidden',
                    touchAction: 'none',
                }}
            >
                {/* Progress fill */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: `${progress * 100}%`,
                    backgroundColor: '#4caf50',
                    borderRadius: '3px 0 0 3px',
                    transition: isScrubbing ? 'none' : 'width 0.05s linear',
                }} />
                {/* Playhead */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: `${progress * 100}%`,
                    width: '2px',
                    height: '100%',
                    backgroundColor: '#fff',
                    transform: 'translateX(-1px)',
                }} />
            </div>

            {/* Duration */}
            <div style={styles.fieldRow}>
                <span style={styles.label}>Duration</span>
                <input
                    type="number"
                    value={data.duration ?? 10}
                    min={0}
                    step={0.1}
                    onChange={(e) => handleDurationChange(parseFloat(e.target.value) || 0)}
                    style={{ ...styles.input, flex: 1 }}
                />
            </div>

            {/* Speed */}
            <div style={styles.fieldRow}>
                <span style={styles.label}>Speed</span>
                <input
                    type="number"
                    value={data.speed ?? 1}
                    min={0}
                    step={0.1}
                    onChange={(e) => handleSpeedChange(parseFloat(e.target.value) || 0)}
                    style={{ ...styles.input, flex: 1 }}
                />
            </div>

            {/* Loop */}
            <div style={styles.fieldRow}>
                <span style={styles.label}>Loop</span>
                <input
                    type="checkbox"
                    checked={data.loop ?? false}
                    onChange={(e) => handleLoopChange(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
            </div>

            {/* Play On Start */}
            <div style={styles.fieldRow}>
                <span style={styles.label}>Play On Start</span>
                <input
                    type="checkbox"
                    checked={data.playOnStart ?? true}
                    onChange={(e) => handlePlayOnStartChange(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
            </div>
        </div>
    );
};
